import useDate from 'src/CrudManager/Core/composables/useDate'
import { usePinnedSharesStore } from 'src/CrudManager/Core/stores/usePinnedSharesStore'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '../stores/useNotificationStore'
import type { ActionExecutionResult, GroupActionPayload, Notification, NotificationAction, NotificationActionPayload, NotificationFilters } from '../types'
import { useActionRegistry } from './useActionRegistry'
import { useNotificationApi } from './useNotificationApi'
import { useNotificationRealTime } from './useNotificationRealTime'

export function useNotificationCenter(userId?: string, autoLoad = true, enableRealtime = true) {
  const store = useNotificationStore()
  const api = useNotificationApi()
  const realTime = useNotificationRealTime()
  const router = useRouter()
  const { formatDate, timeAgo, formatTimeSpent } = useDate()
  const actionRegistry = useActionRegistry()

  const isVisible = ref(false)
  const selectedNotificationId = ref<string | null>(null)
  const currentTab = ref<'all' | 'unread' | 'progressive' | 'mandatory'>('all')

  const loadNotifications = async (page = 1, reset = false) => {
    try {
      store.setLoading(true)
      store.setError(null)

      const filters: NotificationFilters = {}

      if (currentTab.value === 'unread') {
        filters.read = false
      } else if (currentTab.value === 'progressive') {
        filters.progressive = true
      } else if (currentTab.value === 'mandatory') {
        filters.mandatory = true
      }

      const result = await api.fetchNotifications(page, store.perPage, filters)

      if (reset || page === 1) {
        store.setNotifications(result.data)
      } else {
        // Append for pagination
        const existing = store.notifications
        store.setNotifications([...existing, ...result.data])
      }

      store.setPagination(result.current_page, result.last_page, result.per_page, result.total)
      store.setLastSync()
    } catch (error) {
      store.setError('Failed to load notifications')
    } finally {
      store.setLoading(false)
    }
  }

  const refreshNotifications = () => loadNotifications(1, true)

  const loadMore = () => {
    if (store.currentPage < store.totalPages && !store.loading) {
      loadNotifications(store.currentPage + 1, false)
    }
  }

  const markNotificationRead = async (notification: Notification) => {
    if (notification.read_at) return

    try {
      await api.markNotificationAsRead(notification.id)
      store.markAsRead(notification.id)
      showSuccessNotification('Notification marked as read')
    } catch (error) {
      showErrorNotification('Failed to mark notification as read')
    }
  }

  const markNotificationUnread = async (notification: Notification) => {
    if (!notification.read_at) return

    try {
      await api.markNotificationAsUnread(notification.id)
      store.markAsUnread(notification.id)
      showSuccessNotification('Notification marked as unread')
    } catch (error) {
      showErrorNotification('Failed to mark notification as unread')
    }
  }

  const markAllRead = async () => {
    try {
      await api.markAllNotificationsAsRead()
      store.markAllAsRead()
      showSuccessNotification('All notifications marked as read')
    } catch (error) {
      showErrorNotification('Failed to mark all notifications as read')
    }
  }

  const deleteNotification = async (notification: Notification) => {
    try {
      await api.deleteNotification(notification.id)
      store.removeNotification(notification.id)
      showSuccessNotification('Notification deleted')
    } catch (error) {
      showErrorNotification('Failed to delete notification')
    }
  }

  const executeAction = async (
    notification: Notification,
    actionName: string,
    action: NotificationAction,
    actionData?: Record<string, any>,
  ): Promise<ActionExecutionResult> => {
    try {
      if (action.type === 'frontend') {
        // Use the action registry for frontend actions
        const registryResult = await actionRegistry.executeAction(action.handler, notification, actionData)

        // Convert registry result to ActionExecutionResult format
        const result: ActionExecutionResult = {
          success: registryResult.success,
          message: registryResult.message,
          error: registryResult.error,
          redirect_url: registryResult.redirectUrl,
          data: registryResult.data,
        }

        if (result.success) {
          showSuccessNotification(result.message || 'Action executed successfully')

          // Mark notification as read after successful action
          if (!notification.read_at) {
            store.markAsRead(notification.id)
          }

          // Handle redirect if provided
          if (result.redirect_url) {
            await router.push(result.redirect_url)
          }
        } else {
          showErrorNotification(result.error || 'Action execution failed')
        }

        return result
      } else {
        // Keep existing backend action handling
        return await executeBackendAction(notification, actionName, actionData)
      }
    } catch (error) {
      const result: ActionExecutionResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Action execution failed',
      }
      showErrorNotification(result.error!)
      return result
    }
  }

  const executeBackendAction = async (notification: Notification, actionName: string, actionData?: Record<string, any>): Promise<ActionExecutionResult> => {
    const payload: NotificationActionPayload = {
      notification_id: notification.id,
      action_name: actionName,
      action_data: actionData,
    }

    const result = await api.executeNotificationAction(payload)

    if (result.success) {
      showSuccessNotification(result.message || 'Action executed successfully')

      // Mark notification as read after successful action
      if (!notification.read_at) {
        store.markAsRead(notification.id)
      }

      // Handle redirect if provided
      if (result.redirect_url) {
        await router.push(result.redirect_url)
      }
    }

    return result
  }

  const executeGroupAction = async (groupId: string, actionName: string, actionData?: Record<string, any>): Promise<ActionExecutionResult> => {
    try {
      const payload: GroupActionPayload = {
        group_id: groupId,
        action_name: actionName,
        action_data: actionData,
      }

      const result = await api.executeGroupAction(payload)

      if (result.success) {
        showSuccessNotification(result.message || 'Group action executed successfully')
      }

      return result
    } catch (error) {
      const result: ActionExecutionResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Group action execution failed',
      }
      showErrorNotification(result.error!)
      return result
    }
  }

  const retryProgressiveNotification = async (notification: Notification) => {
    try {
      const result = await api.retryProgressiveAction(notification.id)
      if (result.success) {
        showSuccessNotification('Progressive action retried successfully')
        await refreshNotifications()
      }
      return result
    } catch (error) {
      showErrorNotification('Failed to retry progressive action')
      return { success: false, error: 'Retry failed' }
    }
  }

  const cancelProgressiveNotification = async (notification: Notification) => {
    try {
      await api.cancelProgressiveNotification(notification.id)
      showSuccessNotification('Progressive notification cancelled')
      await refreshNotifications()
    } catch (error) {
      showErrorNotification('Failed to cancel progressive notification')
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    selectedNotificationId.value = notification.id

    // Mark as read on click
    if (!notification.read_at) {
      markNotificationRead(notification)
    }

    // Crud model shared notifications get pinned to the floating panel
    // so the recipient can keep them visible while working.
    const data = notification.data as Record<string, unknown> | undefined
    if (data?.type === 'crud_model_shared') {
      const pinned = usePinnedSharesStore()
      pinned.pinFromNotification({
        id: notification.id,
        created_at: notification.created_at ?? null,
        data,
      })
      isVisible.value = false
      return
    }

    // Navigate to related model if available
    if (notification.model_type && notification.model_id) {
      router.push(`/editor/${notification.model_type}/${notification.model_id}`)
    }
  }

  const setTab = (tab: typeof currentTab.value) => {
    currentTab.value = tab
    refreshNotifications()
  }

  // Computed properties for formatted display
  const getNotificationTime = (notification: Notification) => {
    return formatDate(notification.created_at, true)
  }

  const getNotificationTimeAgo = (notification: Notification) => {
    return timeAgo(notification.created_at)
  }

  const getProgressPercentage = (notification: Notification) => {
    if (!notification.progress_data?.progress) return 0
    return Math.max(0, Math.min(100, notification.progress_data.progress))
  }

  const getProgressStatus = (notification: Notification) => {
    return notification.progress_status || 'pending'
  }

  const isActionAvailable = (notification: Notification, action: NotificationAction) => {
    if (action.disabled) return false

    if (action.show_when && notification.progress_status) {
      return action.show_when === notification.progress_status
    }

    return true
  }

  const getAvailableActions = (notification: Notification) => {
    if (!notification.data.actions) return []

    return Object.entries(notification.data.actions)
      .filter(([, action]) => isActionAvailable(notification, action))
      .map(([name, action]) => ({ name, ...action }))
  }

  // Initialize action registry and auto-load notifications
  if (autoLoad && userId) {
    // Initialize action registry first
    actionRegistry.initialize()

    // Only load notifications if store hasn't been initialized yet
    if (!store.initialized) {
      loadNotifications()
    }
  }

  // Setup real-time notifications
  if (enableRealtime) {
    watch(
      () => realTime.isConnected,
      (connected) => {
        if (connected) {
          console.log('Real-time notifications connected')
        }
      },
    )
  }

  // Watch for tab changes
  watch(currentTab, () => {
    refreshNotifications()
  })

  return {
    // State
    store,
    isVisible,
    selectedNotificationId,
    currentTab,

    // Real-time
    realTime,

    // Actions
    loadNotifications,
    refreshNotifications,
    loadMore,
    markNotificationRead,
    markNotificationUnread,
    markAllRead,
    deleteNotification,
    executeAction,
    executeGroupAction,
    retryProgressiveNotification,
    cancelProgressiveNotification,
    handleNotificationClick,
    setTab,

    // Registry
    actionRegistry,

    // Utilities
    getNotificationTime,
    getNotificationTimeAgo,
    getProgressPercentage,
    getProgressStatus,
    isActionAvailable,
    getAvailableActions,

    // Computed
    notifications: computed(() => store.filteredNotifications),
    unreadCount: computed(() => store.unreadCount),
    loading: computed(() => store.loading),
    error: computed(() => store.error),
    hasMore: computed(() => store.currentPage < store.totalPages),
    hasMandatory: computed(() => store.hasUnreadMandatory),
  }
}
